import { defineFieldAdapter, type FieldTransformers } from '@libs/shared/frontend/helpers/define-field-adapter.helper';
import {
  isNonNullish as rIsNonNullish,
  mapValues as rMapValues,
  piped as rPiped,
  when as rWhen,
  pipe as rPipe,
  pickBy as rPickBy,
} from 'remeda';

import { CampaignRequestEditStatusEnum, type components } from '@/libs/openapi';
import { toLocalDate, toLowerDenomination, toUpperDenomination, toDateOnlyString } from '@/utils/convert.util';
import { formatDate, formatEnumValue, formatNumber } from '@/utils/format.util';

export const fromApiCampaignEditRequestFieldAdapter = defineFieldAdapter({
  fields: {
    status: formatEnumValue(CampaignRequestEditStatusEnum),
    createdAt: formatDate('withTime'),
    updatedAt: formatDate('withTime'),
    newEndDate: {
      forLabel: rWhen(rIsNonNullish<string | null | undefined>, formatDate('full')),
      forFormValue: rWhen(rIsNonNullish<string | null | undefined>, toLocalDate),
    },
    newTotalBudget: {
      forLabel: rWhen(rIsNonNullish<number | null | undefined>, rPiped(toUpperDenomination, formatNumber('currency'))),
      forFormValue: rWhen(rIsNonNullish<number | null | undefined>, toUpperDenomination),
    },
    newMonthlyBudget: {
      forLabel: rWhen(rIsNonNullish<Record<string, number> | null | undefined>, (record) => Object.fromEntries(Object.entries(record).map(([ k, v ]) => [ k, formatNumber('currency')(toUpperDenomination(v)) ]))),
      forFormValue: rWhen(rIsNonNullish<Record<string, number> | null | undefined>, rMapValues<Record<string, number>, number>(toUpperDenomination)),
    },
  } satisfies FieldTransformers<components['schemas']['CampaignRequestEditResponseDto']>,
  meta: {
    getCreatedByFullName: (row: components['schemas']['CampaignRequestEditResponseDto']) => `${row.createdBy?.firstName ?? ''} ${row.createdBy?.lastName ?? ''}`.trim(),
  },
}, 'strict');

export const toApiCampaignEditRequestFieldAdapter = defineFieldAdapter({
  fields: {
    newEndDate: rWhen(rIsNonNullish<string | null | undefined>, toDateOnlyString),
    newTotalBudget: rWhen(rIsNonNullish<number | null | undefined>, toLowerDenomination),
    newMonthlyBudget: rWhen(rIsNonNullish<Record<string, number> | null | undefined>, (budget) => rPipe(
      budget,
      rMapValues(toLowerDenomination),
      rPickBy((value) => value !== 0),
    )),
  } satisfies FieldTransformers<Omit<components['schemas']['CampaignRequestEditDto'], 'newMonthlyBudget'> & { newMonthlyBudget?: Record<string, number> | null }>,
}, 'loose');
